import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { medicalDefaultTime } from "../../../../components/RecurringShift/RecurringShiftMasterSchedule/utils";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useGlobalManualLocationOrder } from "../../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useJobs } from "../../../../hooks/useJobs";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { backendService } from "../../../../services/backend.service";
import { censusBoundLabel } from "../../../../utils/censusResolution";
import {
	type DepartmentLocation,
	type DepartmentType,
	EditDepartmentLocationConfigMode,
	NurseFullRole,
	type NurseRole,
	type RatioLadder,
	type ShiftType,
	StatsMode,
} from "../../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../../utils/utilFunctions";
import { useDeleteDepartmentLocations, useGetPccRooms, useUpdateSingleDepartmentLocation } from "../../../Settings/DepartmentConfiguration/forms/hooks";
import { DEFAULT_DEPARTMENT_LOCATION, WEEKDAYS } from "../../../Settings/DepartmentConfiguration/utils";
import {
	type CensusScopeProblem,
	censusChoiceAfterAreaChange,
	censusChoiceAfterNamePick,
	censusScopeProblem,
	type EmrAutofill,
	type EmrMapping,
	initialDepartmentWideCensus,
	offeredEmrMapping,
	pickLocationName,
	uniqueLocationNames,
} from "./locationEmrMapping";
import {
	findRatioLadderProblem,
	formatLadderDays,
	type LadderCoverageGap,
	type LadderDimension,
	type RatioLadderProblem,
	seedRatioLadderFromTargets,
	toLocationRoles,
} from "./ratioLadderCoverage";
import { diffShiftMatchKey, pickShiftMatchKey, type ShiftMatchChange, type ShiftMatchKey, willCascadeOntoShifts } from "./shiftMatchKey";

export type DayAmountRow = {
	id: string;
	/** short day names: ["sun", "mon", ...] */
	days: string[];
	amount: number;
	amountRequired: number;
};

export type LocationConfigSlimData = {
	name: string;
	section: string;
	roles: (NurseFullRole | "all")[];
	/** Job ID for this location (used when job_management_enabled FF is on) */
	jobId: string;
	/** HH:MM format */
	shiftStartTime: string;
	/** HH:MM format */
	shiftEndTime: string;
	/** short lowercase names of the days of the week — derived from dayAmountRows */
	days: string[];
	/** Department type for Synergy MS mode */
	departmentType?: DepartmentType;
	/** Unit associated with this location */
	unit?: string;
	/** Floor associated with this location */
	floor?: string;
	/** Ratio ladder configuration for this location */
	ratioLadder: RatioLadder[];
	/** Per-day staff amounts, keyed by day abbreviation — derived from dayAmountRows */
	dayAmounts: Record<string, number>;
	/** Per-day required amounts, keyed by day abbreviation — derived from dayAmountRows */
	dayAmountsRequired: Record<string, number>;
	/** Grouped rows: each row has days + amount + amountRequired */
	dayAmountRows: DayAmountRow[];
	/** PCC rooms selected for this location */
	rooms: string[];
	/** Admin flag: staff here are tied to the location (e.g. a 1:1 sitter) and excluded from the moveable ratio pool */
	cantMove: boolean;
};

/** Derives flat dayAmounts, dayAmountsRequired, and days from dayAmountRows */
const syncRowsToFlatMaps = (rows: DayAmountRow[]) => {
	const dayAmounts: Record<string, number> = {};
	const dayAmountsRequired: Record<string, number> = {};
	const allDays: string[] = [];
	for (const row of rows) {
		for (const day of row.days) {
			dayAmounts[day] = row.amount;
			dayAmountsRequired[day] = row.amountRequired;
			if (!allDays.includes(day)) allDays.push(day);
		}
	}
	const days = WEEKDAYS.filter((d) => allDays.includes(d));
	return { dayAmounts, dayAmountsRequired, days };
};

const getFormConfigData = (
	isMedicalDepartment: boolean,
	mode: EditDepartmentLocationConfigMode,
	existingConfig?: DepartmentLocation,
	initialSection?: string,
): LocationConfigSlimData => {
	if (existingConfig) {
		// Read per-day amounts directly from the location's dayAmounts JSONB
		const dayAmounts: Record<string, number> = {};
		const dayAmountsRequired: Record<string, number> = {};
		const days = existingConfig.days ?? [...WEEKDAYS];

		for (const day of days) {
			const entry = existingConfig.dayAmounts?.[day];
			dayAmounts[day] = entry?.amount ?? 1;
			dayAmountsRequired[day] = entry?.amount_required ?? 1;
		}

		const dayAmountRows = groupDaysByAmount(days, dayAmounts, dayAmountsRequired).map((group) => ({
			id: uuidv4(),
			days: group.days,
			amount: group.amount,
			amountRequired: group.amountRequired,
		}));
		return {
			name: existingConfig.location ?? "",
			section: existingConfig.section ?? "",
			roles: existingConfig.roles ?? [],
			jobId: existingConfig.jobId ?? "",
			shiftStartTime: isMedicalDepartment ? medicalDefaultTime : (existingConfig.shift_start.substring(0, 5) ?? "06:00"),
			shiftEndTime: isMedicalDepartment ? medicalDefaultTime : (existingConfig.shift_end.substring(0, 5) ?? "18:00"),
			days,
			departmentType: existingConfig.departmentType,
			unit: existingConfig.unit,
			floor: existingConfig.floor,
			ratioLadder: existingConfig.ratioLadder ?? [],
			dayAmounts,
			dayAmountsRequired,
			dayAmountRows,
			rooms: (existingConfig.rooms ?? []).map((r) => r.room_name),
			// DEFAULT: false — covers locations cached/loaded before the field existed; the server backfills it.
			cantMove: existingConfig.cantMove ?? false,
		};
	}
	if (mode === EditDepartmentLocationConfigMode.Create) {
		const dayAmounts: Record<string, number> = {};
		const dayAmountsRequired: Record<string, number> = {};
		for (const day of WEEKDAYS) {
			dayAmounts[day] = 1;
			dayAmountsRequired[day] = 1;
		}
		return {
			name: "",
			section: initialSection ?? "",
			roles: [],
			jobId: "",
			shiftStartTime: "06:00",
			shiftEndTime: "18:00",
			days: [...WEEKDAYS],
			departmentType: undefined,
			unit: undefined,
			floor: undefined,
			ratioLadder: [],
			dayAmounts,
			dayAmountsRequired,
			dayAmountRows: [{ id: uuidv4(), days: [...WEEKDAYS], amount: 1, amountRequired: 1 }],
			rooms: [],
			cantMove: false,
		};
	}
	throw new Error("Invalid mode or missing existing config");
};

/**
 * A ratio location is staffed only by its ladder, so one without a ladder opens with a 0–∞ ladder
 * seeded from its staffing targets (a new location's "every day, 1" target gives 0–∞ × 1). Nothing is
 * saved until the admin clicks Save.
 */
const getInitialConfigData = (
	isRatioDepartment: boolean,
	departmentRoles: NurseRole[],
	...formArgs: Parameters<typeof getFormConfigData>
): LocationConfigSlimData => {
	const data = getFormConfigData(...formArgs);
	if (!isRatioDepartment || data.ratioLadder.length > 0) return data;
	const owner = { roles: toLocationRoles(data.roles, departmentRoles), jobId: data.jobId };
	return { ...data, ratioLadder: seedRatioLadderFromTargets(data.dayAmountRows, owner) };
};

const slimDataToDepartmentLocation = (data: LocationConfigSlimData, departmentShiftTypes: ShiftType[], locationId?: string): Partial<DepartmentLocation> => {
	const dayAmounts: Record<string, { amount: number; amount_required: number }> = {};
	for (const day of data.days) {
		dayAmounts[day] = {
			amount: data.dayAmounts[day] ?? 1,
			amount_required: data.dayAmountsRequired[day] ?? 1,
		};
	}
	return {
		location: data.name.trim(),
		section: data.section.trim(),
		dayAmounts,
		amount: Math.max(...Object.values(data.dayAmounts), 0),
		amountRequired: Math.max(...Object.values(data.dayAmountsRequired), 0),
		roles: data.roles,
		jobId: data.jobId,
		shift_start: data.shiftStartTime,
		shift_end: data.shiftEndTime,
		days: data.days,
		shift_type:
			departmentShiftTypes[
				getShiftIndexByShiftTimes({
					startTime: DateTime.fromFormat(data.shiftStartTime, "HH:mm"),
					endTime: DateTime.fromFormat(data.shiftEndTime, "HH:mm"),
				})
			],
		departmentType: data.departmentType,
		unit: data.unit,
		floor: data.floor,
		ratioLadder: data.ratioLadder.map((rl) => ({
			id: rl.id || uuidv4(),
			location_id: locationId ?? "",
			roles: rl.roles,
			jobId: rl.jobId,
			censusStart: rl.censusStart,
			censusEnd: rl.censusEnd,
			amount: rl.amount,
			days: rl.days,
		})),
		rooms: data.rooms.map((roomName) => ({
			id: uuidv4(),
			schedule_template_id: locationId ?? "",
			room_name: roomName,
		})),
		cantMove: data.cantMove,
	};
};

/**
 * Groups days by (amount, amountRequired) pair.
 * Returns an array of [days[], amount, amountRequired] tuples.
 */
const groupDaysByAmount = (
	days: string[],
	dayAmounts: Record<string, number>,
	dayAmountsRequired: Record<string, number>,
): Array<{ days: string[]; amount: number; amountRequired: number }> => {
	const groups = new Map<string, string[]>();
	for (const day of days) {
		const amt = dayAmounts[day] ?? 1;
		const req = dayAmountsRequired[day] ?? 1;
		const key = `${amt}-${req}`;
		const existing = groups.get(key);
		if (existing) {
			existing.push(day);
		} else {
			groups.set(key, [day]);
		}
	}
	return Array.from(groups.entries()).map(([key, groupDays]) => {
		const [amountStr, reqStr] = key.split("-");
		return {
			days: groupDays,
			amount: Number.parseInt(amountStr, 10),
			amountRequired: Number.parseInt(reqStr, 10),
		};
	});
};

/** For ratio departments: compute target (max) and required (min) from ratio ladder entries per day */
const computeDayAmountsFromRatioLadder = (
	ratioLadder: RatioLadder[],
): { dayAmounts: Record<string, number>; dayAmountsRequired: Record<string, number>; days: string[] } => {
	const dayAmounts: Record<string, number> = {};
	const dayAmountsRequired: Record<string, number> = {};

	for (const entry of ratioLadder) {
		const entryDays = entry.days ?? [...WEEKDAYS];
		for (const day of entryDays) {
			if (day in dayAmounts) {
				dayAmounts[day] = Math.max(dayAmounts[day], entry.amount);
				dayAmountsRequired[day] = Math.min(dayAmountsRequired[day], entry.amount);
			} else {
				dayAmounts[day] = entry.amount;
				dayAmountsRequired[day] = entry.amount;
			}
		}
	}

	const days = WEEKDAYS.filter((d) => d in dayAmounts);
	return { dayAmounts, dayAmountsRequired, days };
};

/**
 * `AnalyticValues` only carries flat primitives, so an error has to be reduced to a message before
 * it can be sent. (The deprecated `trackErrorEvent` did this internally.)
 */
const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * In ratio departments the ladder is the source of truth for per-day amounts, so recompute them
 * from it before building a payload. Shared by the save and create handlers.
 */
const resolveDayAmounts = (data: LocationConfigSlimData, canEditRatioLadder: boolean): LocationConfigSlimData => {
	// An empty ladder never reaches here in a ratio department: save is blocked until it covers 0–∞.
	if (!canEditRatioLadder) return data;
	const computed = computeDayAmountsFromRatioLadder(data.ratioLadder);
	const computedRows = groupDaysByAmount(computed.days, computed.dayAmounts, computed.dayAmountsRequired).map((group) => ({
		id: uuidv4(),
		...group,
	}));
	return { ...data, ...computed, dayAmountRows: computedRows };
};

/** "11–15", "31–∞", or a single census "0" when a gap is one value wide. */
const gapRangeLabel = (gap: LadderCoverageGap): string =>
	gap.start === gap.end ? censusBoundLabel(gap.start) : `${censusBoundLabel(gap.start)}–${censusBoundLabel(gap.end)}`;

/**
 * The text for a ladder that can't be saved. `nameRoles` prefixes a gap with its role, for locations
 * whose ladder is checked role by role.
 */
/** Why Save waits on the census choice, as the Save tooltip says it. */
const CENSUS_SCOPE_PROBLEM_KEY: Record<CensusScopeProblem, string> = {
	missing: "schedule.template.census.scope.required",
	rooms_loading: "schedule.template.census.scope.rooms.loading",
	rooms_failed: "schedule.template.census.scope.rooms.failed",
};

const ratioLadderProblemMessage = (problem: RatioLadderProblem, t: TFunction, nameRoles: boolean): string => {
	if (problem.kind !== "gap") return t(`schedule.template.ratio.ladder.error.${problem.kind}`);
	const when =
		problem.gap.days.length === WEEKDAYS.length
			? t("schedule.template.ratio.ladder.gap.days.all")
			: t("schedule.template.ratio.ladder.gap.days.some", { days: formatLadderDays(problem.gap.days) });
	const params = { range: gapRangeLabel(problem.gap), when };
	return problem.gap.role !== null && nameRoles
		? t("schedule.template.ratio.ladder.error.gap.role", { ...params, role: problem.gap.role })
		: t("schedule.template.ratio.ladder.error.gap", params);
};

/**
 * `base` sits between the defaults and the form data: the edit path passes the existing config so
 * server-owned fields it does not render survive the round trip, the create path passes `{}`.
 */
const buildLocation = (
	data: LocationConfigSlimData,
	id: string,
	shiftTypes: ShiftType[],
	departmentId: string,
	base: Partial<DepartmentLocation>,
): DepartmentLocation =>
	({
		...DEFAULT_DEPARTMENT_LOCATION,
		...base,
		...slimDataToDepartmentLocation(data, shiftTypes, id),
		id,
		department_id: departmentId,
	}) satisfies DepartmentLocation;

export type LocationUpdateInfo = {
	didEdit?: boolean;
	refreshDepartmentData?: boolean;
};

export type EditDepartmentLocationConfigProps = {
	mode: EditDepartmentLocationConfigMode;
	existingConfig?: DepartmentLocation;
	onDidChangeNotifier?: () => void;
	onCancel: () => void;
	onSave: (updatedInfo?: LocationUpdateInfo) => void;
	/** Which days are occupied by existing shifts 0-6 (sun-sat) */
	occupiedDays: number[];
	uniqueSections: string[];
	onSomeDropdownOpenChange: (isOpen: boolean) => void;
	isSynergyMS: boolean;
	/** Create-mode only: pre-select this section (e.g. the section band the "+" was clicked on). */
	initialSection?: string;
	/**
	 * Hide the editable location-NAME text field (default false). For surfaces where the name is owned
	 * by a dedicated rename flow (the Locations page): the config editor still saves the existing name
	 * untouched, it just can't be edited inline here. Additive/gated — existing callers are unaffected.
	 */
	hideNameField?: boolean;
};

export const useEditDepartmentLocationConfigState = ({
	existingConfig,
	mode,
	onSave,
	onDidChangeNotifier,
	occupiedDays,
	onCancel,
	uniqueSections,
	onSomeDropdownOpenChange,
	isSynergyMS = false,
	initialSection,
}: EditDepartmentLocationConfigProps) => {
	const { timezone, chosenDepartment, setChosenDepartment, hospitalData, isMedicalDepartment, showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const enabledJobs = useJobs();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { t } = useTranslation();
	const canEditRatioLadder = chosenDepartment.statsMode === StatsMode.Ratio;

	// --- State ---
	const [configSlimData, setConfigSlimData] = useState<LocationConfigSlimData>(() =>
		getInitialConfigData(canEditRatioLadder, chosenDepartment.roles, isMedicalDepartment, mode, existingConfig, initialSection),
	);
	const [departmentWideCensus, setDepartmentWideCensus] = useState<boolean>(() => initialDepartmentWideCensus(existingConfig, canEditRatioLadder));
	/** The last name pick's EMR fill-in, so picking a name without a mapping can take it back. */
	const emrAutofillRef = useRef<EmrAutofill | null>(null);
	/**
	 * What the popup suggested when it opened, frozen at mount — our only handle on the match key
	 * the orphaned recurring shifts still carry. Snapshotted rather than read back off
	 * `existingConfig` because `getInitialConfigData` rewrites the times for medical departments,
	 * which would otherwise read as divergence on every one of them.
	 */
	const suggestedMatchKeyRef = useRef<ShiftMatchKey>(pickShiftMatchKey(configSlimData));
	const [updateFromDate, setUpdateFromDate] = useState<DateTime>(() => DateTime.now().setZone(timezone).startOf("day"));
	const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<HTMLElement | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
	const [editTimeOpen, setEditTimeOpen] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [uniqueLocationsOptions, setUniqueLocationsOptions] = useState<string[]>(() =>
		initialSection && !uniqueSections.includes(initialSection) ? [...uniqueSections, initialSection] : uniqueSections,
	);
	const [selectedSectionOption, setSelectedSectionOption] = useState<string>(configSlimData.section);

	// --- Memos ---
	const locationRoles = useMemo<NurseRole[]>(
		() => toLocationRoles(configSlimData.roles, chosenDepartment.roles),
		[configSlimData.roles, chosenDepartment.roles],
	);

	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const locationNameOptions = useMemo<string[]>(
		() => uniqueLocationNames(chosenDepartment.locations, configSlimData.name, customStringOrderMap),
		[chosenDepartment.locations, configSlimData.name, customStringOrderMap],
	);

	const locationJobId = useMemo<string>(() => configSlimData.jobId, [configSlimData.jobId]);

	/**
	 * The Weekly and Synergy tabs open this popup in Create mode on an "out of config" row,
	 * pre-filled from a synthetic DepartmentLocation that carries no id. Adopting that suggestion
	 * as-is is safe — the created config matches the orphaned recurring shifts by value — but
	 * diverging from it is not, because POST /departments/locations never touches recurring_shift
	 * or nurse_in_shift.
	 */
	const isOutOfConfigAdoption = mode === EditDepartmentLocationConfigMode.Create && !!existingConfig && !existingConfig.id;

	const shiftMatchChanges = useMemo<ShiftMatchChange[]>(
		() => (isOutOfConfigAdoption ? diffShiftMatchKey(suggestedMatchKeyRef.current, pickShiftMatchKey(configSlimData), jobManagementEnabled) : []),
		[isOutOfConfigAdoption, configSlimData, jobManagementEnabled],
	);
	/** Saving would orphan existing shifts unless we follow the create with a cascading edit. */
	const willUpdateExistingShifts = shiftMatchChanges.length > 0;

	/**
	 * Edit mode only: whether the save will actually reach existing shifts. The server skips the
	 * cascade when no shift-relevant field moved, which makes the "apply from" date inert — so the
	 * picker is disabled rather than silently ignored. An out-of-config create always cascades (that
	 * is the point of its second leg), so it is not gated.
	 */
	const applyFromDateAffectsShifts = useMemo(
		() => (mode === EditDepartmentLocationConfigMode.Edit && existingConfig?.id ? willCascadeOntoShifts(existingConfig, configSlimData) : true),
		[mode, existingConfig, configSlimData],
	);

	// --- Mutations ---
	const {
		mutateAsync: deleteDepartmentLocations,
		isPending: deleteLoading,
		isSuccess: deleteSuccess,
	} = useDeleteDepartmentLocations({
		id: existingConfig?.id ?? "",
	});

	const { mutateAsync: updateSingleLocation, isPending: updateSingleLocationLoading } = useUpdateSingleDepartmentLocation();
	// DEFAULT: [] — until the PCC rooms query answers there are no rooms to list. The census check reads
	// the query's own state instead, so it never mistakes "not loaded yet" for "no rooms".
	const { data: availableRooms = [], isLoading: roomsLoading, isError: roomsFailed } = useGetPccRooms();
	/** The area the popup counts and saves: only the unit, floor and rooms the EMR lists offer. */
	const offeredMapping = useMemo<EmrMapping>(
		() =>
			offeredEmrMapping(
				{ unit: configSlimData.unit, floor: configSlimData.floor, rooms: configSlimData.rooms },
				{
					// DEFAULT: the lists are absent until the department payload loads; null keeps saved values until then.
					units: hospitalData?.units ?? null,
					floors: hospitalData?.floors ?? null,
					rooms: roomsLoading || roomsFailed ? null : availableRooms,
				},
			),
		[configSlimData.unit, configSlimData.floor, configSlimData.rooms, hospitalData?.units, hospitalData?.floors, roomsLoading, roomsFailed, availableRooms],
	);

	// --- Effects ---

	// Auto-populate jobId when editing an existing location that has roles but no job yet
	const didAutoPopulateJobId = useRef(false);
	useEffect(() => {
		if (didAutoPopulateJobId.current) return;
		if (!jobManagementEnabled || configSlimData.jobId || !enabledJobs.length) return;
		if (mode === EditDepartmentLocationConfigMode.Edit && existingConfig?.roles?.length) {
			didAutoPopulateJobId.current = true;
			const firstJobId = enabledJobs[0].id;
			setConfigSlimData((prev) => ({ ...prev, jobId: firstJobId }));
		}
	}, [jobManagementEnabled, configSlimData.jobId, enabledJobs, mode, existingConfig?.roles?.length]);

	useEffect(() => {
		if (deleteSuccess && existingConfig?.id) {
			setChosenDepartment((prevState) => ({
				...prevState,
				locations: prevState.locations.filter((loc) => loc.id !== existingConfig.id),
			}));
			onSave();
		}
	}, [deleteSuccess, setChosenDepartment, existingConfig, onSave]);

	// --- Handlers ---
	const handleDeleteLocation = useCallback(async () => {
		if (existingConfig) {
			await deleteDepartmentLocations();
		}
	}, [deleteDepartmentLocations, existingConfig]);

	/**
	 * Save handler for per-day amounts (edit mode).
	 * Groups days by (amount, amountRequired) and updates/creates/deletes location entries.
	 */
	const handleSaveWithDayAmounts = useCallback(async () => {
		setSaving(true);
		try {
			track("schedule_template_day_amounts_save_start", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
				days: configSlimData.days.join(","),
			});

			if (!existingConfig) return;

			const dataForSave = { ...resolveDayAmounts(configSlimData, canEditRatioLadder), ...offeredMapping };
			const updatedLocation = buildLocation(dataForSave, existingConfig.id, chosenDepartment.shift_types, chosenDepartment.department_id, existingConfig);

			const applyFromDate = updateFromDate.toFormat("yyyy-MM-dd");
			const res = await updateSingleLocation({ updatedLocation, applyFromDate });
			const hasUpdatedShifts = (res?.amountOfUpdatedShifts ?? 0) > 0;

			setChosenDepartment((prevState) => ({
				...prevState,
				locations: prevState.locations.map((loc) => (loc.id === existingConfig.id ? updatedLocation : loc)),
			}));

			track("schedule_template_day_amounts_save_success", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
			});

			onSave({ didEdit: true, refreshDepartmentData: hasUpdatedShifts });
		} catch (error) {
			trackError("schedule_template_day_amounts_save_error", {
				department_id: chosenDepartment.department_id,
				error_message: errorMessage(error),
			});
			showErrorSnackbar("schedule.template.save.error", {}, error);
		} finally {
			setSaving(false);
		}
	}, [
		canEditRatioLadder,
		chosenDepartment.department_id,
		chosenDepartment.shift_types,
		configSlimData,
		offeredMapping,
		existingConfig,
		setChosenDepartment,
		onSave,
		updateSingleLocation,
		updateFromDate,
		showErrorSnackbar,
		track,
		trackError,
	]);

	/**
	 * Second leg of the out-of-config adoption: the config now exists under the *suggested* match
	 * key, so PUT it to the user's real values. That endpoint — unlike the bulk POST — cascades the
	 * change onto the matching recurring shifts and their generated shifts.
	 *
	 * Owns its own error reporting and never rethrows, so a handled failure here does not also
	 * raise the caller's generic save error.
	 */
	const performOutOfConfigAdoption = useCallback(
		async (dataForCreate: LocationConfigSlimData, newLocationId: string, createdLocations: DepartmentLocation[]) => {
			const department_id = chosenDepartment.department_id;
			try {
				// Build on the row the server just returned, not on the defaults, so anything it
				// assigned during the create survives the edit.
				const createdLocation = createdLocations.find((loc) => loc.id === newLocationId);
				if (!createdLocation) {
					throw new Error("The created location is missing from the create response");
				}
				const updatedLocation = buildLocation(dataForCreate, newLocationId, chosenDepartment.shift_types, department_id, createdLocation);
				const applyFromDate = updateFromDate.toFormat("yyyy-MM-dd");
				// Marks the PUT specifically, not the handler as a whole — `..._create_start` already
				// fired before the POST, so without this a session that dies mid-flight cannot be
				// placed on either side of the second call.
				track("schedule_template_out_of_config_adopt_start", {
					department_id,
					location_id: newLocationId,
					location_name: dataForCreate.name,
					apply_from_date: applyFromDate,
					shift_match_changes: shiftMatchChanges.map((change) => change.field).join(","),
				});
				// Deliberately the service, not useUpdateSingleDepartmentLocation: that hook's onError
				// always raises its own `location.config.update.error` snackbar, which would stack on
				// top of the message this orchestration owns. Same reason the create above posts
				// through backendService directly.
				const res = await backendService.updateSingleDepartmentLocation(department_id, { updatedLocation, applyFromDate });
				const amountOfUpdatedShifts = res.data.amountOfUpdatedShifts;
				setChosenDepartment((prevState) => ({
					...prevState,
					locations: createdLocations.map((loc) => (loc.id === newLocationId ? updatedLocation : loc)),
				}));
				track("schedule_template_out_of_config_adopt_success", {
					department_id,
					location_name: dataForCreate.name,
					amount_of_updated_shifts: amountOfUpdatedShifts,
				});
				onSave({ didEdit: true, refreshDepartmentData: amountOfUpdatedShifts > 0 });
			} catch (adoptError) {
				// No transaction spans the two calls, so undo the config we just created and leave
				// the user where they started.
				try {
					await backendService.deleteDepartmentLocations(department_id, newLocationId);
					trackError("schedule_template_out_of_config_adopt_error", { department_id, error_message: errorMessage(adoptError) });
					showErrorSnackbar("schedule.template.updateShifts.failed", {}, adoptError);
				} catch (rollbackError) {
					// Both calls failed: a config now exists under the suggested name that did not
					// before. Commit it and say so exactly, rather than "nothing was changed".
					// Carry the PUT failure too — it is the reason the rollback ran, and this branch
					// is the only place it would otherwise go unrecorded.
					trackError("schedule_template_out_of_config_adopt_rollback_error", {
						department_id,
						error_message: errorMessage(rollbackError),
						adopt_error_message: errorMessage(adoptError),
					});
					setChosenDepartment((prevState) => ({ ...prevState, locations: createdLocations }));
					showErrorSnackbar("schedule.template.updateShifts.rollback.failed", { locationName: suggestedMatchKeyRef.current.name }, rollbackError);
					onSave({ didEdit: true, refreshDepartmentData: true });
				}
			}
		},
		[
			chosenDepartment.department_id,
			chosenDepartment.shift_types,
			updateFromDate,
			setChosenDepartment,
			onSave,
			showErrorSnackbar,
			shiftMatchChanges,
			track,
			trackError,
		],
	);

	/**
	 * Create handler for per-day amounts.
	 * Groups days by (amount, amountRequired) and creates one location per group.
	 */
	const handleCreateWithDayAmounts = useCallback(async () => {
		setSaving(true);
		try {
			track("schedule_template_day_amounts_create_start", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
				days: configSlimData.days.join(","),
			});

			const dataForCreate = { ...resolveDayAmounts(configSlimData, canEditRatioLadder), ...offeredMapping };
			const newLocationId = uuidv4();
			// When the user diverged from the suggestion, create under the *suggested* match key so
			// the new config lines up with the orphaned recurring shifts; the adoption step below
			// then moves both to the values they actually asked for.
			const dataForInitialCreate: LocationConfigSlimData = willUpdateExistingShifts ? { ...dataForCreate, ...suggestedMatchKeyRef.current } : dataForCreate;
			const newLocation = buildLocation(dataForInitialCreate, newLocationId, chosenDepartment.shift_types, chosenDepartment.department_id, {});

			const response = await backendService.updateDepartmentLocations(chosenDepartment.department_id, {
				new: [newLocation],
				existing: chosenDepartment.locations,
			});
			if (!response.data?.length) {
				throw new Error("Creating the location returned no locations");
			}

			if (willUpdateExistingShifts) {
				await performOutOfConfigAdoption(dataForCreate, newLocationId, response.data);
				return;
			}

			setChosenDepartment((prevState) => ({
				...prevState,
				locations: response.data,
			}));

			track("schedule_template_day_amounts_create_success", {
				department_id: chosenDepartment.department_id,
				location_name: configSlimData.name,
			});

			onSave();
		} catch (error) {
			trackError("schedule_template_day_amounts_create_error", {
				department_id: chosenDepartment.department_id,
				error_message: errorMessage(error),
			});
			showErrorSnackbar("schedule.template.save.error", {}, error);
		} finally {
			setSaving(false);
		}
	}, [
		canEditRatioLadder,
		chosenDepartment.department_id,
		chosenDepartment.locations,
		chosenDepartment.shift_types,
		configSlimData,
		offeredMapping,
		setChosenDepartment,
		onSave,
		showErrorSnackbar,
		willUpdateExistingShifts,
		performOutOfConfigAdoption,
		track,
		trackError,
	]);

	const didChange = useRef<boolean>(false);
	const handleSetConfigSlimData = useCallback(
		(newData: Partial<LocationConfigSlimData>) => {
			setConfigSlimData((prevState) => {
				const updated = { ...prevState, ...newData };
				return updated;
			});
			if (!didChange.current) {
				didChange.current = true;
				if (onDidChangeNotifier) {
					onDidChangeNotifier();
				}
			}
		},
		[onDidChangeNotifier],
	);

	// Sync ratio ladder entry roles/jobId when location roles/job change
	const prevLocationRolesRef = useRef<NurseRole[]>(locationRoles);
	const prevLocationJobIdRef = useRef<string>(locationJobId);
	useEffect(() => {
		if (!canEditRatioLadder || configSlimData.ratioLadder.length === 0) return;

		if (jobManagementEnabled) {
			const prev = prevLocationJobIdRef.current;
			prevLocationJobIdRef.current = locationJobId;
			if (prev === locationJobId) return;

			handleSetConfigSlimData({
				ratioLadder: configSlimData.ratioLadder.map((entry) => ({
					...entry,
					jobId: locationJobId,
				})),
			});
		} else {
			const prev = prevLocationRolesRef.current;
			prevLocationRolesRef.current = locationRoles;
			const prevSet = new Set(prev.map(String));
			const currSet = new Set(locationRoles.map(String));
			if (prevSet.size === currSet.size && [...prevSet].every((r) => currSet.has(r))) return;

			if (locationRoles.length <= 1) {
				handleSetConfigSlimData({
					ratioLadder: configSlimData.ratioLadder.map((entry) => ({
						...entry,
						roles: [...locationRoles],
					})),
				});
			} else {
				handleSetConfigSlimData({
					ratioLadder: configSlimData.ratioLadder.map((entry) => {
						const filtered = entry.roles.filter((r) => currSet.has(String(r)));
						return {
							...entry,
							roles: filtered.length > 0 ? filtered : [...locationRoles],
						};
					}),
				});
			}
		}
	}, [locationRoles, locationJobId, jobManagementEnabled, canEditRatioLadder, configSlimData.ratioLadder, handleSetConfigSlimData]);

	const handleAddRow = useCallback(() => {
		const newRow: DayAmountRow = { id: uuidv4(), days: [], amount: 1, amountRequired: 1 };
		const newRows = [...configSlimData.dayAmountRows, newRow];
		const synced = syncRowsToFlatMaps(newRows);
		handleSetConfigSlimData({ dayAmountRows: newRows, ...synced });
	}, [configSlimData.dayAmountRows, handleSetConfigSlimData]);

	const handleRemoveRow = useCallback(
		(rowId: string) => {
			const newRows = configSlimData.dayAmountRows.filter((r) => r.id !== rowId);
			const synced = syncRowsToFlatMaps(newRows);
			handleSetConfigSlimData({ dayAmountRows: newRows, ...synced });
		},
		[configSlimData.dayAmountRows, handleSetConfigSlimData],
	);

	const handleUpdateRow = useCallback(
		(rowId: string, updates: Partial<DayAmountRow>) => {
			const newRows = configSlimData.dayAmountRows.map((row) => {
				if (row.id !== rowId) return row;
				const updated = { ...row, ...updates };
				// Sort days in WEEKDAYS order
				if (updates.days) {
					updated.days = WEEKDAYS.filter((d) => updated.days.includes(d));
				}
				// Clamp values to non-negative
				updated.amount = Math.max(0, updated.amount);
				updated.amountRequired = Math.max(0, updated.amountRequired);
				// Clamp amountRequired when amount changes
				if (updates.amount !== undefined) {
					updated.amountRequired = Math.min(updated.amountRequired, updates.amount);
				}
				return updated;
			});
			const synced = syncRowsToFlatMaps(newRows);
			handleSetConfigSlimData({ dayAmountRows: newRows, ...synced });
		},
		[configSlimData.dayAmountRows, handleSetConfigSlimData],
	);

	const rowHasOccupiedDays = useMemo(() => {
		const occupiedShortDays = new Set<string>(occupiedDays.map((idx) => WEEKDAYS[idx]).filter(Boolean));
		const result: Record<string, boolean> = {};
		for (const row of configSlimData.dayAmountRows) {
			result[row.id] = row.days.some((day) => occupiedShortDays.has(day));
		}
		return result;
	}, [occupiedDays, configSlimData.dayAmountRows]);

	const disabledDaysPerRow = useMemo(() => {
		const result: Record<string, string[]> = {};
		for (const row of configSlimData.dayAmountRows) {
			const otherDays = configSlimData.dayAmountRows.filter((r) => r.id !== row.id).flatMap((r) => r.days);
			result[row.id] = otherDays;
		}
		return result;
	}, [configSlimData.dayAmountRows]);

	const handleUpdateFromDate = useCallback(
		(newDate: DateTime | null) => {
			if (newDate) {
				setUpdateFromDate(newDate);
				if (!didChange.current) {
					didChange.current = true;
					if (onDidChangeNotifier) {
						onDidChangeNotifier();
					}
				}
			}
		},
		[onDidChangeNotifier],
	);

	const editTimePopoverParentRef = useRef<HTMLDivElement>(null);
	const handleSelectedRoleFiltersChanged = useCallback(
		(newSelectedRoles: (NurseRole | "all")[]) => {
			handleSetConfigSlimData({
				roles: newSelectedRoles.map((r) => (r === "all" ? "all" : NurseFullRole[r])),
			});
		},
		[handleSetConfigSlimData],
	);

	const handleSelectedJobChanged = useCallback(
		(newJobId: string) => {
			handleSetConfigSlimData({ jobId: newJobId });
		},
		[handleSetConfigSlimData],
	);

	const times = useMemo(() => {
		const start = DateTime.fromFormat(configSlimData.shiftStartTime, "HH:mm").setZone(timezone);
		const end = DateTime.fromFormat(configSlimData.shiftEndTime, "HH:mm").setZone(timezone);
		return {
			start: start,
			end: end <= start ? end.plus({ day: 1 }) : end,
		};
	}, [configSlimData.shiftStartTime, configSlimData.shiftEndTime, timezone]);

	/** Why the ladder can't be saved yet (ratio departments only), or null once it covers 0–∞. */
	const ratioLadderProblem = useMemo<RatioLadderProblem | null>(() => {
		if (!canEditRatioLadder) return null;
		const dimension: LadderDimension = jobManagementEnabled ? { kind: "job" } : { kind: "roles", roles: locationRoles };
		return findRatioLadderProblem(configSlimData.ratioLadder, {
			dimension,
			requireJobPerRow: jobManagementEnabled && enabledJobs.length > 1,
			requireRolesPerRow: !jobManagementEnabled && chosenDepartment.roles.length > 1,
		});
	}, [canEditRatioLadder, jobManagementEnabled, locationRoles, configSlimData.ratioLadder, enabledJobs.length, chosenDepartment.roles.length]);
	const ratioLadderProblemText = useMemo<string | null>(
		() => (ratioLadderProblem === null ? null : ratioLadderProblemMessage(ratioLadderProblem, t, !jobManagementEnabled && locationRoles.length > 1)),
		[ratioLadderProblem, t, jobManagementEnabled, locationRoles.length],
	);

	// DEFAULT: hospitalData is undefined until the department payload loads; until then there are no EMR areas to offer.
	const availableUnits = hospitalData?.units ?? [];
	const availableFloors = hospitalData?.floors ?? [];
	const emrOptionsAvailable = availableUnits.length > 0 || availableFloors.length > 0 || availableRooms.length > 0;
	const censusScope = censusScopeProblem({
		unitsOrFloorsAvailable: availableUnits.length > 0 || availableFloors.length > 0,
		rooms: { state: roomsLoading ? "loading" : roomsFailed ? "failed" : "loaded", count: availableRooms.length },
		mapping: offeredMapping,
		departmentWideCensus,
	});
	// While the rooms list holds Save back, the EMR section has nothing to show yet, so the popup says why in its place.
	const roomsNotice = censusScope === "rooms_loading" || censusScope === "rooms_failed" ? censusScope : null;

	const saveDisabledReason = useMemo(() => {
		if (!configSlimData.name || configSlimData.name.trim() === "") {
			return "Location name is required.";
		}
		if (!times.start.isValid || !times.end.isValid) {
			return "Valid shift times are required.";
		}
		if (!isSynergyMS) {
			if (jobManagementEnabled) {
				if (!configSlimData.jobId) {
					return "A job must be selected.";
				}
			} else if (configSlimData.roles.length === 0) {
				return "At least one role must be selected.";
			}
		}
		if (censusScope !== null) {
			return t(CENSUS_SCOPE_PROBLEM_KEY[censusScope]);
		}
		if (canEditRatioLadder) {
			return ratioLadderProblemText;
		}
		for (const row of configSlimData.dayAmountRows) {
			if (row.days.length === 0) {
				return "Each row must have at least one day selected.";
			}
			if (row.amountRequired > row.amount) {
				return "Required amount cannot exceed the amount of staff for each day.";
			}
		}
		if (configSlimData.days.length === 0) {
			return "At least one day must be selected.";
		}
		return null;
	}, [
		configSlimData.name,
		times,
		configSlimData.roles.length,
		configSlimData.jobId,
		jobManagementEnabled,
		isSynergyMS,
		configSlimData.dayAmountRows,
		configSlimData.days,
		canEditRatioLadder,
		ratioLadderProblemText,
		censusScope,
		t,
	]);

	const isSavedEnabled = useMemo(() => {
		return !saveDisabledReason;
	}, [saveDisabledReason]);

	const addSectionOption = useCallback(
		(newOption: string) => {
			const trimmed = newOption.trim();
			if (!uniqueLocationsOptions.find((val) => val.toLowerCase() === trimmed.toLowerCase())) {
				const updatedOptions = [...uniqueLocationsOptions, trimmed];
				setUniqueLocationsOptions(updatedOptions);
			}
			setSelectedSectionOption(trimmed);
			handleSetConfigSlimData({ section: trimmed });
		},
		[uniqueLocationsOptions, handleSetConfigSlimData],
	);
	const ondDropdownOptionSelect = useCallback(
		(selectedValue?: string | undefined) => {
			if (selectedValue != null /*dont want null or undefined but its ok to have no section aka empty string*/) {
				const trimmed = selectedValue.trim();
				setSelectedSectionOption(trimmed);
				handleSetConfigSlimData({ section: trimmed });
			}
		},
		[handleSetConfigSlimData],
	);
	/**
	 * Picking or typing a name. An existing name reuses how that location is already mapped to the EMR;
	 * every field stays editable. The dropdown only calls this with a non-blank, trimmed value.
	 */
	const handleSelectLocationName = useCallback(
		(picked: string | undefined) => {
			if (picked == null) return; // the name is required, so the dropdown offers no "clear"
			const pick = pickLocationName(chosenDepartment.locations, picked, configSlimData.name, existingConfig?.id);
			if (pick === null) return;
			const current = {
				departmentWideCensus,
				mapping: { unit: configSlimData.unit, floor: configSlimData.floor, rooms: configSlimData.rooms },
			};
			const { choice, autofill } = censusChoiceAfterNamePick(current, pick.mapping, emrAutofillRef.current);
			emrAutofillRef.current = autofill;
			setDepartmentWideCensus(choice.departmentWideCensus);
			handleSetConfigSlimData({ name: pick.name, ...choice.mapping });
		},
		[
			chosenDepartment.locations,
			configSlimData.name,
			configSlimData.unit,
			configSlimData.floor,
			configSlimData.rooms,
			departmentWideCensus,
			existingConfig?.id,
			handleSetConfigSlimData,
		],
	);
	/**
	 * Editing the unit, floor or rooms. Choosing an area unticks Department wide — reachable only where the
	 * fields stay open while it is ticked (PPD/HPPD; see `LocationEmrMappingSection`).
	 */
	const handleEmrMappingChange = useCallback(
		(changes: Partial<EmrMapping>) => {
			const current = { departmentWideCensus, mapping: { unit: configSlimData.unit, floor: configSlimData.floor, rooms: configSlimData.rooms } };
			setDepartmentWideCensus(censusChoiceAfterAreaChange(current, changes).departmentWideCensus);
			handleSetConfigSlimData(changes);
		},
		[departmentWideCensus, configSlimData.unit, configSlimData.floor, configSlimData.rooms, handleSetConfigSlimData],
	);
	/** Department wide and an EMR area are exclusive: ticking it clears unit, floor and rooms. */
	const handleDepartmentWideCensusChange = useCallback(
		(checked: boolean) => {
			setDepartmentWideCensus(checked);
			handleSetConfigSlimData(checked ? { unit: undefined, floor: undefined, rooms: [] } : {});
		},
		[handleSetConfigSlimData],
	);
	const handleSomeDropdownOpen = useCallback(() => onSomeDropdownOpenChange(true), [onSomeDropdownOpenChange]);
	const handleSomeDropdownClose = useCallback(() => onSomeDropdownOpenChange(false), [onSomeDropdownOpenChange]);

	return {
		configSlimData,
		updateFromDate,
		datePickerAnchorEl,
		setDatePickerAnchorEl,
		editTimePopoverParentRef,
		editTimeOpen,
		setEditTimeOpen,
		times,
		handleSelectedRoleFiltersChanged,
		isSavedEnabled,
		mode,
		existingConfig,
		onCancel,
		handleDeleteLocation,
		deleteLoading,
		setConfirmDelete,
		confirmDelete,
		updateDepartmentLocationsLoading: deleteLoading || updateSingleLocationLoading || saving,
		handleSetConfigSlimData,
		handleUpdateFromDate,
		occupiedDays,
		allowDelete: occupiedDays.length === 0,
		addSectionOption,
		selectedSectionOption,
		uniqueLocationsOptions,
		ondDropdownOptionSelect,
		handleSomeDropdownOpen,
		handleSomeDropdownClose,
		isSynergyMS,
		saveDisabledReason,
		availableUnits,
		availableFloors,
		availableRooms,
		emrOptionsAvailable,
		roomsNotice,
		offeredMapping,
		departmentWideCensus,
		handleDepartmentWideCensusChange,
		handleEmrMappingChange,
		locationNameOptions,
		handleSelectLocationName,
		canEditRatioLadder,
		locationRoles,
		ratioLadderGapText: ratioLadderProblem?.kind === "gap" ? ratioLadderProblemText : null,
		handleAddRow,
		handleRemoveRow,
		handleUpdateRow,
		disabledDaysPerRow,
		handleSaveWithDayAmounts,
		handleCreateWithDayAmounts,
		shiftMatchChanges,
		willUpdateExistingShifts,
		applyFromDateAffectsShifts,
		rowHasOccupiedDays,
		jobManagementEnabled,
		handleSelectedJobChanged,
		locationJobId,
	};
};
